import { MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { AppConfig } from './../../app.config';
import { CurrencyUtil, isValidNumber, isValidNumberRange } from '@liquitics/transaction';
import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { SearchBarTerm, HistoricalSearchBarTerm } from './search-bar-term';
import { Component, OnInit, Input, ElementRef, ViewChild } from '@angular/core';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { startWith, map } from 'rxjs/operators';
import { SearchBarTermOption } from './search-bar-term-option';
import * as moment from 'moment';
import { MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { LocalDBService } from '../../service/local-db.service';

const SEARCH_BAR_LATEST_STATE_KEY_PREFIX = 'SearchBarLatestState-';
const SEARCH_BAR_HISTORICAL_KEY_PREFIX = 'SearchBarHistorical-';

@Component({
    selector: 'app-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.scss'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    ],
})
export class SearchBarComponent implements OnInit {
    searchBarTerms: SearchBarTerm[] = [];
    historicalSearches: HistoricalSearchBarTerm[] = [];

    @Input()
    searchContextName: string;

    @Input()
    placeholder: string;
    @Input()
    searchBarTermOptions: SearchBarTermOption[];

    separatorKeysCodes: number[] = [ENTER, COMMA];

    @ViewChild('searchTermInput', { static: true }) searchTermInput: ElementRef;
    @ViewChild('searchTermInputDate', { static: true }) searchTermInputDate: ElementRef;
    searchTermCtrl = new FormControl();
    autoCompleteValues: Observable<string[]>;
    operatingDateM: moment.Moment;
    browserLanguage: string;
    hasErrors: boolean;
    searching: boolean;

    constructor(private appConfig: AppConfig,
        private localDB: LocalDBService,
        private adapter: DateAdapter<any>) {
        this.autoCompleteValues = this.searchTermCtrl.valueChanges.pipe(
            startWith(null),
            map((searchTermString: string | null) => searchTermString
                ? this.autoCompleteValuesFor(searchTermString)
                : this.searchBarTermOptions.map((sbto: SearchBarTermOption) => { return sbto.name })));
        this.operatingDateM = moment(appConfig.getTodayString());
        this.browserLanguage = navigator.language;
        this.adapter.setLocale(this.browserLanguage);
    }

    getSearchBarTerms(): SearchBarTerm[] {
        return this.searchBarTerms;
    }

    debug() {
        console.log(JSON.stringify(this.searchBarTerms, null, 2));
    }

    ngOnInit() {
        const latestState = this.localDB.getItem(`${SEARCH_BAR_LATEST_STATE_KEY_PREFIX}${this.searchContextName}`);
        if (latestState) {
            this.searchBarTerms = JSON.parse(latestState);
            this.searchBarTerms
                .forEach((sbt: SearchBarTerm) => {
                    this.repopulate(sbt);
                });
        }
        const historicalSearches = this.localDB.getItem(`${SEARCH_BAR_HISTORICAL_KEY_PREFIX}${this.searchContextName}`);
        if (historicalSearches) {
            this.historicalSearches = JSON.parse(historicalSearches);
            this.historicalSearches
                .forEach((hsbt: HistoricalSearchBarTerm) => {
                    hsbt.searchBarTerms.forEach((sbt: SearchBarTerm) => {
                        this.repopulate(sbt);
                    })
                });
        }
    }

    repopulate(sbt: SearchBarTerm): void {
        if (sbt.option) {
            // Reset the object as the parsed object only has the serializable data, not the registered functionality
            sbt.option = this.findSearchBarTermOption(sbt.option.name);
        }
    }

    populateHistoricalSearch(hsbt: HistoricalSearchBarTerm): void {
        const searchBarTerms = hsbt.searchBarTerms;
        this.searchBarTerms = searchBarTerms;
    }

    findSearchBarTermOption(optionName: string): SearchBarTermOption {
        return this.searchBarTermOptions.find((option: SearchBarTermOption) => {
            return option.name.toUpperCase() === optionName.toUpperCase();
        });
    }

    createSearchTerm(termStr: string) {
        const searchTerm = {
        } as SearchBarTerm;

        if (termStr) {
            termStr = termStr.trim();
        }
        const parsedValues = this.parseSearchTermValue(termStr);
        if (parsedValues.length > 1) {
            // Has key : value alread, set the value to the parsed & trimmed raw user string
            searchTerm.value = parsedValues[1].trim();
        }
        const foundOption = this.findSearchBarTermOption(parsedValues[0]);
        if (foundOption) {
            // Valid option at lease
            searchTerm.name = foundOption.name;
            searchTerm.active = true;
            searchTerm.option = foundOption;
            if (searchTerm.value) {
                // First see if we have a better case sensitive version of the value
                const validValues = foundOption.validValues();
                if (validValues && validValues.length > 0) {
                    const foundValue = validValues.find((validValue: string) => {
                        return validValue.toUpperCase() === searchTerm.value.toUpperCase();
                    });
                    if (foundValue) {
                        // We found it, replace it so we have the better pre-formatted value
                        searchTerm.value = foundValue;
                    } else {
                        // Configuration says there is a valid set of values.
                        if (!foundOption.freeValueAllowed) {
                            // Free form values arenot allowed.
                            searchTerm.active = false;
                            searchTerm.invalid = true;
                            searchTerm.errorMessage = `${searchTerm.value} is not an allowed value for ${foundOption.name}`;
                            this.hasErrors = true;
                        }
                    }
                }
                // Lets see if they are parsable types
                if (foundOption.dataType === 'amount') {
                    if (!isValidNumber(searchTerm.value)) {
                        searchTerm.active = false;
                        searchTerm.invalid = true;
                        searchTerm.errorMessage = `${searchTerm.value} is not a valid amount`;
                        this.hasErrors = true;
                    }
                } else if (foundOption.dataType === 'timerange') {
                    if (!isValidNumberRange(searchTerm.value)) {
                        searchTerm.active = false;
                        searchTerm.invalid = true;
                        searchTerm.errorMessage = `${searchTerm.value} is not a valid number range (ie: 1-5)`;
                        this.hasErrors = true;
                    }
                } else if (foundOption.dataType === 'boolean') {
                    if ((searchTerm.value !== 'true') && (searchTerm.value !== 'false')) {
                        searchTerm.active = false;
                        searchTerm.invalid = true;
                        searchTerm.errorMessage = `Value: ${searchTerm.value} must be 'true' or 'false'`;
                        this.hasErrors = true;
                    }
                }

                if (!searchTerm.invalid) {
                    // Now let's validate the value if still valid
                    const errorMessage = foundOption.validateValue(searchTerm.value);
                    if (errorMessage) {
                        searchTerm.active = false;
                        searchTerm.invalid = true;
                        searchTerm.errorMessage = errorMessage;
                        this.hasErrors = true;
                    }
                }
            }
        } else {
            // Unregistered option, no good.
            searchTerm.active = false;
            searchTerm.invalid = true;
            searchTerm.name = parsedValues[0];
            searchTerm.errorMessage = `${parsedValues[0]} is not a valid option`;
        }
        return searchTerm;
    }

    add(event: MatChipInputEvent): void {
        const input = event.input;
        const value = event.value;
        if (!value) {
            this.resetForNextInput();
            return;
        }
        const splits = value.split(':');
        if (splits.length > 1) {
            if (splits[1].trim().length === 0) {
                // Key: + empty value (user input still in progress...)
                return;
            }
        } else {
            // No value, user input still in progress...
            return;
        }
        const searchTerm = this.createSearchTerm(value);
        this.searchBarTerms.push(searchTerm);

        // Reset & ready for next (even if its an invalid entry. Give user ability to edit)
        input.value = '';
        this.resetForNextInput();
        this.saveSearchTermState();
    }

    resetForNextInput() {
        this.searchTermInput.nativeElement.value = '';
        this.searchTermCtrl.setValue(null);
        // setInterval(() => {
        //     this.separatorKeysCodes = [];
        // }, 100)
    }

    remove(searchTerm: SearchBarTerm): void {
        const index = this.searchBarTerms.indexOf(searchTerm);
        if (index >= 0) {
            this.searchBarTerms.splice(index, 1);
        }
        this.saveSearchTermState();
    }

    parseSearchTermValue(termStr: string): string[] {
        const splits = termStr.split(':');
        // if (splits.length === 2) {
        //     return splits;
        // } else {
        //     throw new Error('Search term must be <attribute>:<value>');
        // }
        return splits;
    }

    getErrorHint(): string {
        return this.searchBarTerms
            .filter((st: SearchBarTerm) => {
                return st.invalid;
            })
            .map((st: SearchBarTerm) => {
                return st.errorMessage;
            })
            .join('. ');
    }

    toggleSelection(searchTerm: SearchBarTerm): void {
        if (searchTerm.invalid) {
            // Don't allow activation of invalid terms
            return;
        }
        searchTerm.active = !searchTerm.active;
        this.saveSearchTermState();
    }

    chipColor(searchTerm: SearchBarTerm): string {
        return searchTerm.active ? 'primary' : 'accent';
    }

    getSearchTermChipClass(searchTerm: SearchBarTerm, active: boolean): string {
        let chipClass = 'search-term-chip';
        if (searchTerm && searchTerm.option) {
            chipClass += `-${searchTerm.option.dataType}`;
        }
        if (active) {
            chipClass += ' search-bar-term-selected';
        }
        return chipClass;
    }

    autoCompleteSelected(event: MatAutocompleteSelectedEvent): void {
        if (this.hasKeyBeenSet()) {
            const combinedValue = this.searchTermInput.nativeElement.value.split(':')[0] + ':' + event.option.value;
            this.searchTermInput.nativeElement.value = combinedValue;
            const searchTerm = this.createSearchTerm(combinedValue);
        } else {
            const option = this.findSearchBarTermOption(event.option.value);
            const defaultValue = this.getDefaultValue(option);
            this.searchTermInput.nativeElement.value = event.option.value + ': ' + defaultValue;
            this.searchTermCtrl.setValue(event.option.value + ': ' + defaultValue);
        }
    }

    autoCompleteValuesFor(inputValue: string): string[] {
        if (inputValue.indexOf(':') === -1) {
            // Still populating name
            return this.searchBarTermOptions
                .filter((option: SearchBarTermOption) => {
                    return option.name.toUpperCase().indexOf(inputValue.trim().toUpperCase()) !== -1;
                })
                .map((option: SearchBarTermOption) => {
                    return option.name;
                });
        } else {
            const splits = inputValue.split(':');
            const foundOption = this.findSearchBarTermOption(splits[0]);
            return foundOption.validValues()
                .filter((validValue: string) => {
                    if (splits.length > 1) {
                        return validValue.toUpperCase().indexOf(splits[1].trim().toUpperCase()) !== -1;
                    } else {
                        return true;
                    }
                });
            ;
        }
    }

    hasKeyBeenSet() {
        return this.searchTermInput.nativeElement.value.indexOf(':') !== -1;
    }

    editSelection(searchTerm: SearchBarTerm): void {
        this.searchTermInput.nativeElement.value = searchTerm.name + ': ' + searchTerm.value;
        this.remove(searchTerm);
    }

    getDefaultValue(searchTermOption: SearchBarTermOption): string {
        let defaultValue = '';
        if (searchTermOption.dataType === 'date') {
            defaultValue = this.appConfig.getTodayString();
        } else if (searchTermOption.dataType === 'timerange') {
            defaultValue = '0-1';
        } else if (searchTermOption.dataType === 'boolean') {
            defaultValue = 'true';
        }
        return defaultValue;
    }

    saveSearchTermState() {
        this.localDB.setItem(`${SEARCH_BAR_LATEST_STATE_KEY_PREFIX}${this.searchContextName}`, JSON.stringify(this.searchBarTerms));
    }

    onSearch(historicalSearchDescription: string, searchedSearchBarTerms: SearchBarTerm[]) {
        const historicalSearchBarTerm = {
            displayValue: historicalSearchDescription,
            searchBarTerms: searchedSearchBarTerms
        } as HistoricalSearchBarTerm;
        this.historicalSearches.unshift(historicalSearchBarTerm);
        this.saveHistoricalSearchTermState();
    }

    saveHistoricalSearchTermState() {
        this.localDB.setItem(`${SEARCH_BAR_HISTORICAL_KEY_PREFIX}${this.searchContextName}`, JSON.stringify(this.historicalSearches));
    }

    validateSearchBarTerms(): string | undefined {
        const requiredKeys = this.searchBarTermOptions
            .filter((searchBarTermOption: SearchBarTermOption) => {
                return searchBarTermOption.required;
            })
            .map((searchBarTermOption: SearchBarTermOption) => {
                return searchBarTermOption.name;
            });
        const duplicateViolatedKeys = [];
        const activeKeys = [];
        const keyCounts = this.searchBarTerms
            .filter((sbt: SearchBarTerm) => {
                return sbt.active && !sbt.invalid;
            })
            .reduce((grouped: any, sbt: SearchBarTerm) => {
                let count = grouped[sbt.name] || 0;
                count++;
                grouped[sbt.name] = count;
                if (!sbt.option.multipleAllowed && count === 2) {
                    duplicateViolatedKeys.push(sbt.name);
                }
                activeKeys.push(sbt.name);
                return grouped;
            }, {});
        if (duplicateViolatedKeys.length > 0) {
            return `You may not use ${duplicateViolatedKeys} multiple times.`;
        }
        const missingRequiredKeys = requiredKeys
            .filter((requiredKey: string) => {
                return !activeKeys.includes(requiredKey);
            });
        if (missingRequiredKeys.length > 0) {
            return `You must supply: ${missingRequiredKeys}.`;
        }
    }
}
